// CostBadge — uniform cost-transparency badge used across AI Dialer surfaces.
//
// Renders:  "$spent / $cap"  with yellow at 80% and red at 100% of cap.
// Modes:
//   compact (default) — "$1.23 / $50.00" pill
//   full              — adds a label + percentage line and optional tooltip
//
// Why a shared component: the user mandate is that agents must see the cost
// at every step of the AI Dialer flow. Centralizing the styling keeps the
// warning thresholds and currency formatting consistent.

import React from 'react';
import { DollarSign, AlertTriangle } from 'lucide-react';

function fmt(v) {
  const n = Number(v) || 0;
  if (n > 0 && n < 0.01) return '<$0.01';
  return `$${n.toFixed(2)}`;
}

export default function CostBadge({
  spent = 0,
  cap = 0,
  label,
  mode = 'compact',
  sub,
  className = '',
}) {
  const pct = cap > 0 ? Math.min(100, Math.round((Number(spent) / Number(cap)) * 100)) : 0;
  const danger = cap > 0 && Number(spent) >= Number(cap);
  const warn = !danger && pct >= 80;

  const tone = danger
    ? 'border-red-500/40 bg-red-500/10 text-red-200'
    : warn
    ? 'border-yellow-500/40 bg-yellow-500/10 text-yellow-200'
    : 'border-white/10 bg-white/5 text-white/70';

  if (mode === 'compact') {
    return (
      <span
        data-testid="cost-badge"
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${tone} ${className}`}
        title={label ? `${label}: ${fmt(spent)} / ${fmt(cap)}` : undefined}
      >
        {danger ? <AlertTriangle className="w-3 h-3" /> : <DollarSign className="w-3 h-3" />}
        <span>{fmt(spent)}{cap > 0 ? ` / ${fmt(cap)}` : ''}</span>
      </span>
    );
  }

  return (
    <div
      data-testid="cost-badge-full"
      className={`rounded-xl border p-3 flex items-center gap-3 text-sm ${tone} ${className}`}
    >
      {danger ? <AlertTriangle className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
      <div className="flex-1 min-w-0">
        {label && <div className="font-medium truncate">{label}</div>}
        <div className="text-xs opacity-80">
          {fmt(spent)}{cap > 0 ? ` / ${fmt(cap)} cap (${pct}%)` : ''}
          {sub ? ` · ${sub}` : ''}
        </div>
      </div>
      {danger && <span className="text-[10px] uppercase tracking-wide font-semibold">Cap reached</span>}
    </div>
  );
}
